import * as XLSX from "xlsx";
import moment from "moment";

export const MAX_IMPORT_ROWS = 1000;
export const MAX_RANGE_DAYS = 3660;

export const IMPORT_COLUMNS = [
  {
    key: "codeNo",
    label: "Code No",
    required: true,
    aliases: [
      "codeno",
      "code",
      "codenumber",
      "inventorycode",
      "inventorycodeno",
      "invcode",
      "sitecode",
      "siteno",
      "inventoryno",
    ],
  },
  {
    key: "printingCharge",
    label: "Printing Charge",
    aliases: [
      "printingcharge",
      "printingcharges",
      "printcharge",
      "printcharges",
      "printing",
      "printingcost",
      "pc",
    ],
  },
  {
    key: "mountingCharge",
    label: "Mounting Charge",
    aliases: [
      "mountingcharge",
      "mountingcharges",
      "mountcharge",
      "mountcharges",
      "mounting",
      "mountingcost",
      "mc",
    ],
  },
  {
    key: "specialRatePerMonth",
    label: "Special Rate Per Month",
    aliases: [
      "specialratepermonth",
      "specialratepm",
      "specialratemonth",
      "specialpermonth",
      "spratepermonth",
      "srpm",
    ],
  },
  {
    key: "startDate",
    label: "Start Date",
    required: true,
    aliases: [
      "startdate",
      "start",
      "startingdate",
      "fromdate",
      "from",
      "bookingstart",
      "bookingstartdate",
      "campaignstart",
      "campaignstartdate",
      "startdt",
      "datefrom",
    ],
  },
  {
    key: "endDate",
    label: "End Date",
    required: true,
    aliases: [
      "enddate",
      "end",
      "endingdate",
      "todate",
      "to",
      "bookingend",
      "bookingenddate",
      "campaignend",
      "campaignenddate",
      "enddt",
      "dateto",
    ],
  },
];

const FIELD_BY_KEY = Object.fromEntries(IMPORT_COLUMNS.map((c) => [c.key, c]));

export const normalizeHeader = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const normalizeCode = (value) =>
  String(value ?? "")
    .normalize("NFKC") // full-width letters/digits and similar look-alikes
    .replace(/[\s​-‍⁠﻿]+/g, "")
    .replace(/[-‐-―−﹘﹣－]/g, "") // hyphen, en/em dash, minus etc.
    .toLowerCase();

export const isBlank = (value) =>
  value === null ||
  value === undefined ||
  (typeof value === "string" && value.trim() === "");

const matchHeaderToField = (header, columnList = IMPORT_COLUMNS) => {
  const norm = normalizeHeader(header);
  if (!norm) return null;
  const exact = columnList.find((c) => c.aliases.includes(norm));
  if (exact) return exact.key;
  // "Start Date (DD-MM-YYYY)" style headers: allow a long alias as a prefix.
  let best = null;
  columnList.forEach((c) => {
    c.aliases.forEach((alias) => {
      if (
        alias.length >= 6 &&
        norm.startsWith(alias) &&
        (!best || alias.length > best.len)
      ) {
        best = { key: c.key, len: alias.length };
      }
    });
  });
  return best ? best.key : null;
};

const pad2 = (n) => String(n).padStart(2, "0");

const buildDate = (y, m, d) => {
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) {
    return null;
  }
  if (y < 1990 || y > 2100 || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const probe = new Date(Date.UTC(y, m - 1, d));
  if (
    probe.getUTCFullYear() !== y ||
    probe.getUTCMonth() !== m - 1 ||
    probe.getUTCDate() !== d
  ) {
    return null;
  }
  return `${pad2(d)}-${pad2(m)}-${y}`;
};

const MONTHS = {
  jan: 1,
  january: 1,
  feb: 2,
  february: 2,
  mar: 3,
  march: 3,
  apr: 4,
  april: 4,
  may: 5,
  jun: 6,
  june: 6,
  jul: 7,
  july: 7,
  aug: 8,
  august: 8,
  sep: 9,
  sept: 9,
  september: 9,
  oct: 10,
  october: 10,
  nov: 11,
  november: 11,
  dec: 12,
  december: 12,
};

const fullYear = (y) => (y < 100 ? 2000 + y : y);

/**
 * Returns { blank: true } | { value: "DD-MM-YYYY" } | { error }.
 * Text dates are read day-first (DD-MM-YYYY), matching the rest of the app.
 */
export const parseDateCell = (value, { date1904 = false } = {}) => {
  if (isBlank(value)) return { blank: true };

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return { error: "Invalid date" };
    const built = buildDate(
      value.getFullYear(),
      value.getMonth() + 1,
      value.getDate()
    );
    return built ? { value: built } : { error: "Invalid date" };
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value) || value < 20000 || value > 80000) {
      return { error: `"${value}" is not a valid date` };
    }
    const parts = XLSX.SSF.parse_date_code(Math.floor(value), { date1904 });
    const built = parts ? buildDate(parts.y, parts.m, parts.d) : null;
    return built ? { value: built } : { error: `"${value}" is not a valid date` };
  }

  if (typeof value !== "string") return { error: "Invalid date" };

  const original = value.trim();
  const text = original
    .replace(
      /[T\s]+\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?\s*(Z|[+-]\d{2}:?\d{2}|am|pm)?$/i,
      ""
    )
    .trim();

  let m = text.match(/^(\d{1,2})[-/. ](\d{1,2})[-/. ](\d{2}|\d{4})$/);
  if (m) {
    const built = buildDate(fullYear(+m[3]), +m[2], +m[1]);
    return built
      ? { value: built }
      : { error: `"${original}" is not a valid date (use DD-MM-YYYY)` };
  }

  m = text.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/);
  if (m) {
    const built = buildDate(+m[1], +m[2], +m[3]);
    return built
      ? { value: built }
      : { error: `"${original}" is not a valid date` };
  }

  m = text.match(/^(\d{1,2})(?:st|nd|rd|th)?[-/. ,]+([A-Za-z]{3,9})\.?[-/. ,]+(\d{2}|\d{4})$/);
  if (m && MONTHS[m[2].toLowerCase()]) {
    const built = buildDate(fullYear(+m[3]), MONTHS[m[2].toLowerCase()], +m[1]);
    return built
      ? { value: built }
      : { error: `"${original}" is not a valid date` };
  }

  m = text.match(/^([A-Za-z]{3,9})\.?[-/. ,]+(\d{1,2})(?:st|nd|rd|th)?[-/. ,]+(\d{4})$/);
  if (m && MONTHS[m[1].toLowerCase()]) {
    const built = buildDate(+m[3], MONTHS[m[1].toLowerCase()], +m[2]);
    return built
      ? { value: built }
      : { error: `"${original}" is not a valid date` };
  }

  return { error: `"${original}" is not a valid date (use DD-MM-YYYY)` };
};

/** Returns { blank: true } | { value: number } | { error }. */
export const parseAmountCell = (value, label = "Value") => {
  if (isBlank(value)) return { blank: true };

  let num;
  if (typeof value === "number") {
    num = value;
  } else if (typeof value === "string") {
    const cleaned = value
      .trim()
      .replace(/^(rs\.?|inr)\s*/i, "")
      .replace(/[₹,\s]/g, "")
      .replace(/\/-$/, "");
    if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
      return { error: `${label} "${value.trim()}" is not a number` };
    }
    num = Number(cleaned);
  } else {
    return { error: `${label} is not a number` };
  }

  if (!Number.isFinite(num)) return { error: `${label} is not a number` };
  if (num < 0) return { error: `${label} cannot be negative` };
  return { value: Math.round(num * 100) / 100 };
};

const decodeUtf8 = (bytes) => {
  if (typeof TextDecoder !== "undefined") {
    return new TextDecoder("utf-8").decode(bytes);
  }
  let binary = "";
  for (let i = 0; i < bytes.length; i += 1) binary += String.fromCharCode(bytes[i]);
  try {
    return decodeURIComponent(escape(binary));
  } catch (e) {
    return binary;
  }
};

export const readWorkbook = (buffer) => {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
  const isZip = bytes[0] === 0x50 && bytes[1] === 0x4b;
  const isOle =
    bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0;
  if (isZip || isOle) {
    // cellStyles is what makes SheetJS report hidden rows.
    return XLSX.read(bytes, { type: "array", cellStyles: true });
  }
  // CSV / text: keep values as typed so DD/MM/YYYY is never re-read as MM/DD.
  const text = decodeUtf8(bytes).replace(/^﻿/, "");
  return XLSX.read(text, { type: "string", raw: true });
};

export const sheetToRows = (sheet) => {
  const rows = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    raw: true,
    defval: null,
    blankrows: true,
  });
  while (rows.length > 0 && (rows[rows.length - 1] || []).every(isBlank)) rows.pop();
  const firstRow = sheet["!ref"] ? XLSX.utils.decode_range(sheet["!ref"]).s.r : 0;
  const hidden = new Set();
  (sheet["!rows"] || []).forEach((info, idx) => {
    if (info && info.hidden) hidden.add(idx - firstRow);
  });
  return { rows, firstRow, hidden };
};

const findHeader = (rows, columnList = IMPORT_COLUMNS) => {
  let best = null;
  const limit = Math.min(rows.length, 30);
  for (let r = 0; r < limit; r += 1) {
    const cells = rows[r] || [];
    const columns = {};
    const matched = [];
    const duplicates = [];
    const ignored = [];
    cells.forEach((cell, idx) => {
      if (isBlank(cell)) return;
      const key = matchHeaderToField(cell, columnList);
      if (!key) {
        ignored.push({ header: String(cell).replace(/\s+/g, " ").trim(), col: idx });
      } else if (columns[key] !== undefined) {
        duplicates.push({ header: String(cell).replace(/\s+/g, " ").trim(), key });
      } else {
        columns[key] = idx;
        matched.push({
          key,
          label: FIELD_BY_KEY[key].label,
          header: String(cell).replace(/\s+/g, " ").trim(),
        });
      }
    });
    const score = matched.length;
    if (score > 0 && (!best || score > best.score)) {
      best = { index: r, columns, matched, duplicates, ignored, score };
    }
  }
  return best;
};

const missingRequired = (columns, columnList = IMPORT_COLUMNS) =>
  columnList.filter((c) => c.required && columns[c.key] === undefined).map(
    (c) => c.label
  );

/**
 * Reads an uploaded workbook (xlsx / xls / csv) and returns the rows found
 * under the header row. Column order and header wording do not matter.
 */
export const parseBdfExcelBuffer = (buffer, { columnKeys } = {}) => {
  const columnList = columnKeys
    ? IMPORT_COLUMNS.filter((c) => columnKeys.includes(c.key))
    : IMPORT_COLUMNS;
  let workbook;
  try {
    workbook = readWorkbook(buffer);
  } catch (e) {
    return {
      ok: false,
      error: "This file could not be read. Upload a .xlsx, .xls or .csv file.",
    };
  }

  const sheetNames = workbook.SheetNames || [];
  if (sheetNames.length === 0) {
    return { ok: false, error: "The file has no sheets." };
  }

  const date1904 = Boolean(
    workbook.Workbook && workbook.Workbook.WBProps && workbook.Workbook.WBProps.date1904
  );

  let chosen = null;
  let fallbackReport = null;
  for (const name of sheetNames) {
    const sheet = workbook.Sheets[name];
    if (!sheet) continue;
    const { rows, firstRow, hidden } = sheetToRows(sheet);
    const header = findHeader(rows, columnList);
    if (!header) continue;
    const missing = missingRequired(header.columns, columnList);
    if (missing.length === 0) {
      chosen = { name, rows, firstRow, hidden, header };
      break;
    }
    if (!fallbackReport || header.score > fallbackReport.header.score) {
      fallbackReport = { name, header, missing };
    }
  }

  if (!chosen) {
    if (fallbackReport) {
      return {
        ok: false,
        error: `Missing required column${
          fallbackReport.missing.length > 1 ? "s" : ""
        }: ${fallbackReport.missing.join(", ")}.`,
        foundHeaders: fallbackReport.header.matched
          .map((m) => m.header)
          .concat(fallbackReport.header.ignored.map((i) => i.header)),
      };
    }
    return {
      ok: false,
      error:
        "No header row found. The sheet needs columns for Code No, Start Date and End Date.",
    };
  }

  const { rows, firstRow, hidden, header } = chosen;
  const cols = header.columns;
  const dataRows = [];
  const skipped = [];
  const hiddenInData = [];

  for (let r = header.index + 1; r < rows.length; r += 1) {
    const cells = rows[r] || [];
    const get = (key) => (cols[key] === undefined ? undefined : cells[cols[key]]);
    const rowNumber = firstRow + r + 1;

    const codeCell = get("codeNo");
    const startCell = get("startDate");
    const endCell = get("endDate");

    if (isBlank(codeCell) && isBlank(startCell) && isBlank(endCell)) {
      const hasAny = columnList.some((c) => !isBlank(get(c.key)));
      if (hasAny) skipped.push(rowNumber);
      continue;
    }

    const errors = [];
    const code = isBlank(codeCell) ? "" : String(codeCell).replace(/\s+/g, " ").trim();
    if (!code) errors.push("Code No is required");

    const start = parseDateCell(startCell, { date1904 });
    const end = parseDateCell(endCell, { date1904 });
    if (start.blank) errors.push("Start Date is required");
    else if (start.error) errors.push(`Start Date: ${start.error}`);
    if (end.blank) errors.push("End Date is required");
    else if (end.error) errors.push(`End Date: ${end.error}`);

    const amount = (key, label) => {
      if (cols[key] === undefined) return { blank: true };
      const parsed = parseAmountCell(get(key), label);
      if (parsed.error) errors.push(parsed.error);
      return parsed;
    };
    const printing = amount("printingCharge", "Printing Charge");
    const mounting = amount("mountingCharge", "Mounting Charge");
    const specialRatePerMonth = amount("specialRatePerMonth", "Special Rate Per Month");

    if (hidden.has(r)) hiddenInData.push(rowNumber);
    dataRows.push({
      rowNumber,
      code,
      startDate: start.value || null,
      endDate: end.value || null,
      printingCharge: printing.value ?? null,
      mountingCharge: mounting.value ?? null,
      specialRatePerMonth: specialRatePerMonth.value ?? null,
      errors,
    });
  }

  if (dataRows.length === 0) {
    return {
      ok: false,
      error: "No inventory rows found below the header row.",
    };
  }
  if (dataRows.length > MAX_IMPORT_ROWS) {
    return {
      ok: false,
      error: `The file has ${dataRows.length} rows; at most ${MAX_IMPORT_ROWS} can be imported at once.`,
    };
  }

  const warnings = [];
  header.duplicates.forEach((d) =>
    warnings.push(
      `Column "${d.header}" was ignored because "${FIELD_BY_KEY[d.key].label}" was already found.`
    )
  );
  if (skipped.length > 0) {
    warnings.push(
      `${skipped.length} row${skipped.length > 1 ? "s" : ""} without Code No or dates ${
        skipped.length > 1 ? "were" : "was"
      } skipped (row ${skipped.slice(0, 8).join(", ")}${skipped.length > 8 ? ", ..." : ""}).`
    );
  }

  if (hiddenInData.length > 0) {
    warnings.push(
      `${hiddenInData.length} hidden row${hiddenInData.length > 1 ? "s" : ""} in the sheet ${
        hiddenInData.length > 1 ? "are" : "is"
      } included (row ${hiddenInData.slice(0, 8).join(", ")}${hiddenInData.length > 8 ? ", ..." : ""}).`
    );
  }

  header.ignored.forEach((i) => {
    const n = normalizeHeader(i.header);
    if (
      columnList.some((c) => c.key === "specialRatePerMonth") &&
      ["specialrate", "rpm", "ratepermonth", "rate"].includes(n)
    ) {
      warnings.push(
        `Column "${i.header}" is not used. Name it "Special Rate Per Month" to set the monthly price.`
      );
    }
  });

  return {
    ok: true,
    sheetName: chosen.name,
    headerRowNumber: firstRow + header.index + 1,
    matched: header.matched,
    ignored: header.ignored,
    warnings,
    rows: dataRows,
    hasColumn: Object.fromEntries(
      columnList.map((c) => [c.key, cols[c.key] !== undefined])
    ),
  };
};

export const daysBetween = (startStr, endStr) => {
  const [sd, sm, sy] = startStr.split("-").map(Number);
  const [ed, em, ey] = endStr.split("-").map(Number);
  const start = Date.UTC(sy, sm - 1, sd);
  const end = Date.UTC(ey, em - 1, ed);
  return Math.round((end - start) / 86400000) + 1;
};

export const addDays = (dateStr, n) => {
  const [d, m, y] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${pad2(dt.getUTCDate())}-${pad2(dt.getUTCMonth() + 1)}-${dt.getUTCFullYear()}`;
};

const ymd = (value) => moment(value).format("YYYY-MM-DD");

/**
 * Keeps only the bookings / blocks that can touch the requested period, so the
 * per-day availability check stays fast. The result is identical for every day
 * inside the period because a booking that does not overlap it can never block
 * one of its days.
 */
export const narrowRecordToRange = (record, startDmy, endDmy) => {
  const toYmd = (dmy) => {
    const [d, m, y] = dmy.split("-");
    return `${y}-${m}-${d}`;
  };
  const rangeStart = toYmd(startDmy);
  const rangeEnd = toYmd(endDmy);
  const overlaps = (from, to) => ymd(from) <= rangeEnd && ymd(to) >= rangeStart;
  const narrowList = (list) =>
    Array.isArray(list) ? list.filter((b) => b && overlaps(b.startDate, b.endDate)) : list;
  const narrowBlocks = (list) =>
    Array.isArray(list)
      ? list.filter(
          (b) =>
            b && b.blockedAt && b.blockedTill && b.archived !== true && overlaps(b.blockedAt, b.blockedTill)
        )
      : list;
  const linked = (site) =>
    site && typeof site === "object" ? { ...site, Bookings: narrowList(site.Bookings) } : site;
  return {
    ...record,
    Bookings: narrowList(record.Bookings),
    premiumBlock: narrowList(record.premiumBlock),
    blockedDates: narrowBlocks(record.blockedDates),
    fullId: linked(record.fullId),
    half1Id: linked(record.half1Id),
    half2Id: linked(record.half2Id),
  };
};

/**
 * Turns parsed rows into ready-to-add BDF lines, applying the same rules as
 * adding inventories by hand. `ctx` supplies the creator's own logic.
 */
export const planBdfExcelImport = ({
  rows,
  existingInventories,
  resolveInventory,
  isDateBlocked,
  hasSiblingOverlap,
  calcSpecialRate,
  canAddToCart,
  allowDuplicateInventories,
  packageRate,
  createLine,
  chargesEnabled = true,
}) => {
  const working = [...(existingInventories || [])];
  const seenCodeRows = new Map();
  const planned = [];

  rows.forEach((row) => {
    const errors = [...row.errors];
    const warnings = [];
    const out = {
      rowNumber: row.rowNumber,
      code: row.code,
      location: "",
      startDate: row.startDate,
      endDate: row.endDate,
      printingCharge: null,
      mountingCharge: null,
      specialRatePerMonth: null,
      amount: null,
      defaults: {},
      errors,
      warnings,
      line: null,
    };

    let found = null;
    let unavailable = false;
    if (row.code) {
      found = resolveInventory(row.code);
      if (!found) {
        errors.push(`Code No "${row.code}" was not found`);
      } else if (found.ambiguous) {
        errors.push(`Code No "${row.code}" matches more than one inventory`);
        found = null;
      }
    }

    if (found) {
      out.location = found.inventory.location || "";
      const inv = found.inventory;
      const invKey = normalizeCode(row.code);

      if (!allowDuplicateInventories) {
        if (seenCodeRows.has(invKey)) {
          errors.push(`Duplicate of row ${seenCodeRows.get(invKey)} in this file`);
        } else if (working.some((item) => item && item._id === inv._id)) {
          errors.push("Already added to this BDF");
        } else {
          const check = canAddToCart(
            working.map((item) => item && item.codeNo),
            [inv.codeNo]
          );
          if (!check.canAdd) errors.push(check.reason);
        }
      }

      if (row.startDate && row.endDate) {
        const span = daysBetween(row.startDate, row.endDate);
        if (span < 1) {
          errors.push("End Date is before Start Date");
        } else if (span > MAX_RANGE_DAYS) {
          errors.push("Booking period is longer than 10 years");
        } else if (errors.length === 0) {
          const candidate = createLine(inv);
          const narrowed = narrowRecordToRange(candidate, row.startDate, row.endDate);
          const blockedDays = [];
          for (let i = 0; i < span; i += 1) {
            const day = addDays(row.startDate, i);
            if (isDateBlocked(day, narrowed, working)) blockedDays.push(day);
          }
          // Availability is only a warning: the row is still imported and the
          // BDF creator flags blocked dates when the BDF is created.
          if (blockedDays.length > 0) {
            unavailable = true;
            warnings.push(
              `Dates not available: ${blockedDays.length} day${
                blockedDays.length > 1 ? "s" : ""
              } already booked or blocked (first: ${blockedDays[0]}). Imported without dates, pick new ones`
            );
          } else if (
            allowDuplicateInventories &&
            hasSiblingOverlap(working, candidate, row.startDate, row.endDate)
          ) {
            unavailable = true;
            warnings.push(
              "Dates not available: overlaps another period already added for the same inventory on this BDF. Imported without dates, pick new ones"
            );
          }
        }
      }

      if (errors.length === 0) {
        const ignoreSheetCharges = Boolean(packageRate);
        const base = createLine(inv);
        const pick = (sheetValue, fallback, name) => {
          if (ignoreSheetCharges) return 0;
          if (sheetValue === null || sheetValue === undefined) {
            out.defaults[name] = true;
            return fallback;
          }
          return sheetValue;
        };
        // Without charge columns (e.g. premium block) the inventory's own
        // defaults are kept; a package rate still zeroes the monthly rate.
        const printingCharge = chargesEnabled
          ? pick(row.printingCharge, base.printingCharge, "printingCharge")
          : base.printingCharge;
        const mountingCharge = chargesEnabled
          ? pick(row.mountingCharge, base.mountingCharge, "mountingCharge")
          : base.mountingCharge;
        const specialRatePerMonth = chargesEnabled
          ? pick(row.specialRatePerMonth, base.specialRatePerMonth, "specialRatePerMonth")
          : ignoreSheetCharges
            ? 0
            : base.specialRatePerMonth;

        if (
          chargesEnabled &&
          ignoreSheetCharges &&
          (row.printingCharge !== null ||
            row.mountingCharge !== null ||
            row.specialRatePerMonth !== null)
        ) {
          warnings.push("Charges in the file are ignored because a package rate is set");
        }
        if (
          !ignoreSheetCharges &&
          row.specialRatePerMonth !== null &&
          Number(inv.ratePerMonth) > 0 &&
          row.specialRatePerMonth > Number(inv.ratePerMonth)
        ) {
          warnings.push("Special rate per month is above the card rate (RPM)");
        }

        const line = {
          ...base,
          ...(chargesEnabled ? { printingCharge, mountingCharge } : {}),
          specialRatePerMonth,
          startDate: unavailable ? undefined : row.startDate,
          endDate: unavailable ? undefined : row.endDate,
          // The sheet's dates, kept so the Booking Period cell can show them.
          excelDates: unavailable
            ? { startDate: row.startDate, endDate: row.endDate }
            : undefined,
        };
        if (!unavailable) {
          const { newSpecialRate, discount } = calcSpecialRate(
            line,
            row.startDate,
            row.endDate
          );
          line.specialRate = newSpecialRate || 0;
          line.discount = discount;
        }
        if (chargesEnabled) {
          line._actualPrintingCharge = undefined;
          line._actualMountingCharge = undefined;
        }

        if (chargesEnabled) {
          out.printingCharge = printingCharge;
          out.mountingCharge = mountingCharge;
          out.specialRatePerMonth = specialRatePerMonth;
        }
        out.amount = unavailable ? null : line.specialRate;
        out.line = line;
        seenCodeRows.set(invKey, row.rowNumber);
        working.push(line);
      }
    }

    out.status = errors.length === 0 ? "ready" : "error";
    planned.push(out);
  });

  const ready = planned.filter((p) => p.status === "ready");
  return {
    rows: planned,
    readyCount: ready.length,
    errorCount: planned.length - ready.length,
    lines: ready.map((p) => p.line),
  };
};

/** Header-only template (no sample rows, so nothing can be imported by accident). */
export const buildBdfImportTemplate = ({ columnKeys } = {}) => {
  const wb = XLSX.utils.book_new();
  const columnList = columnKeys
    ? IMPORT_COLUMNS.filter((c) => columnKeys.includes(c.key))
    : IMPORT_COLUMNS;
  const headers = columnList.map((c) => c.label);
  const sheet = XLSX.utils.aoa_to_sheet([headers]);
  sheet["!cols"] = headers.map((h) => ({ wch: Math.max(14, h.length + 4) }));
  XLSX.utils.book_append_sheet(wb, sheet, "Inventories");

  const notes = XLSX.utils.aoa_to_sheet([
    ["How to fill the Inventories sheet"],
    ["Code No, Start Date and End Date are required in every row."],
    ["Dates: use DD-MM-YYYY (for example 05-10-2026) or real Excel dates."],
    ...(columnList.some((c) => c.key === "specialRatePerMonth")
      ? [
          [
            "Printing Charge, Mounting Charge, Special Rate Per Month: leave blank to use the default for that inventory.",
          ],
        ]
      : []),
    ["Amount (In A/C) is calculated from the dates; do not add it."],
    ["Column order does not matter."],
  ]);
  notes["!cols"] = [{ wch: 100 }];
  XLSX.utils.book_append_sheet(wb, notes, "Instructions");
  return XLSX.write(wb, { bookType: "xlsx", type: "array" });
};

